import { useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export default function useHomeMotion(rootRef, reduceMotion) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root || reduceMotion) return;
    let disposed = false;
    let context;
    let timer;
    const start = () => {
      if (disposed || context) return;
      clearTimeout(timer);
      context = gsap.context(() => {
        const media = gsap.matchMedia();
        media.add({ desktop: "(min-width: 768px)", mobile: "(max-width: 767px)", reduced: "(prefers-reduced-motion: reduce)" }, ({ conditions }) => {
          if (conditions.reduced) return;
          const mobile = conditions.mobile;
          const records = [];
          // One reversible timeline per element, with a long neutral reading
          // interval. No fixed positioning, masks, spacers or artificial gaps.
          const scene = (element, from, exit, trigger = element) => {
            if (!element) return;
            const timeline = gsap.timeline({ scrollTrigger: {
              trigger, start: "top bottom", end: "bottom top", scrub: mobile ? .18 : .5,
              invalidateOnRefresh: true,
            } });
            const neutral = { x: 0, y: 0, rotationX: 0, rotationY: 0, rotationZ: 0, scale: 1, opacity: 1 };
            timeline.fromTo(element, { transformPerspective: 1400, transformOrigin: "50% 50%", ...from }, { ...neutral, duration: .24, ease: "power2.out" }, 0)
              .to(element, { ...neutral, duration: .56, ease: "none" }, .24)
              .to(element, { ...neutral, ...exit, duration: .2, ease: "power1.in" }, .8);
            records.push({ element, timeline });
          };
          const intro = gsap.timeline();
          intro.fromTo(".hm-title-line", { yPercent: mobile ? 12 : 105, opacity: mobile ? 0 : 1 }, { yPercent: 0, opacity: 1, duration: mobile ? .3 : .85, stagger: .1, ease: "power3.out" })
            .fromTo(".hm-hero-showcase", { y: mobile ? 8 : 24, opacity: 0, scale: mobile ? 1 : .98 }, { y: 0, opacity: 1, scale: 1, duration: mobile ? .35 : 1.1, ease: "power3.out" }, .15)
            .fromTo(".hm-hero-content .ea-lede, .hm-hero-content .ea-actions, .hm-hero-links", { y: mobile ? 8 : 16, opacity: 0 }, { y: 0, opacity: 1, duration: .5, stagger: .08, ease: "power2.out" }, .45);

          // Editorial headings move as intact blocks, never under an overlay.
          root.querySelectorAll(".ea-section-head, .hm-head-inline, .hm-subhead, .hm-product-section-head, .hm-launch-heading").forEach((element) => {
            scene(element, { y: mobile ? 12 : 32, opacity: .65 }, { y: mobile ? -6 : -16, opacity: .85 });
          });
          // Independent rows avoid revealing an entire tall grid at once.
          root.querySelectorAll(".hm-ai-grid > .ea-feature, .ea-cards > .ea-card, .ea-industries > .ea-industry").forEach((element) => {
            const index = [...element.parentElement.children].indexOf(element);
            const side = index % 2 ? 1 : -1;
            scene(element, { y: mobile ? 12 : 38, x: mobile ? 0 : side * 12, rotationY: mobile ? 0 : side * 5, scale: mobile ? 1 : .975, opacity: .6 }, { y: mobile ? -6 : -14, rotationX: mobile ? 0 : -2, scale: mobile ? 1 : .99, opacity: .85 });
          });
          // Give the specialist and product scenes a deliberate opposing reveal.
          for (const selector of [".hm-specialists-intro", ".hm-cledge-intro"]) {
            scene(root.querySelector(selector), { x: mobile ? 0 : -30, y: mobile ? 12 : 20, rotationY: mobile ? 0 : 4, opacity: .7 }, { y: mobile ? -6 : -12 });
          }
          for (const selector of [".hm-specialists-paths", ".hm-cledge-features", ".hm-launch-workspace"]) {
            scene(root.querySelector(selector), { x: mobile ? 0 : 30, y: mobile ? 12 : 32, rotationY: mobile ? 0 : -5, scale: mobile ? 1 : .975, opacity: .7 }, { y: mobile ? -6 : -16, rotationX: mobile ? 0 : -2 });
          }
          root.querySelectorAll(".hm-process-step").forEach((element) => {
            scene(element, { y: mobile ? 12 : 30, rotationX: mobile ? 0 : 7, opacity: .65 }, { y: mobile ? -6 : -12, opacity: .9 });
          });
          for (const selector of [".hm-fit", ".hm-fit-work", ".hm-about-overview", ".ea-case"]) {
            root.querySelectorAll(selector).forEach((element) => scene(element, { y: mobile ? 12 : 36, scale: mobile ? 1 : .97, opacity: .75 }, { y: mobile ? -6 : -16, scale: mobile ? 1 : .99 }));
          }
          // Settle the complete ancestor chain on keyboard focus so no control
          // inherits a faded or moving parent while the visitor is using it.
          const onFocus = (event) => {
            records.filter(({ element }) => element.contains(event.target)).forEach(({ element, timeline }) => {
              timeline.scrollTrigger?.disable(false);
              timeline.pause();
              gsap.set(element, { x: 0, y: 0, rotationX: 0, rotationY: 0, rotationZ: 0, scale: 1, opacity: 1 });
            });
          };
          root.addEventListener("focusin", onFocus);
          return () => root.removeEventListener("focusin", onFocus);
        });
        return () => media.revert();
      }, root);
      ScrollTrigger.refresh();
    };
    timer = window.setTimeout(start, 700);
    document.fonts?.ready.then(start);
    const refresh = () => ScrollTrigger.refresh();
    root.addEventListener("load", refresh, true);
    return () => {
      disposed = true;
      clearTimeout(timer);
      root.removeEventListener("load", refresh, true);
      context?.revert();
    };
  }, [rootRef, reduceMotion]);
}
